/**
 * The household's care cost where two people are in care (owner, 7 Oct 2026: step 3 "is only
 * baselining against one person"). The engine prices each room on its own schedule; everything a
 * reader sees as "what care costs" must add the two. One helper, so no screen adds them differently.
 *
 * The result keeps every field of the FIRST room's schedule (breakdowns, fee arrangements, the
 * room's own DAP for its own rows) and replaces only the household totals.
 */
export interface CareCostTotals {
    totalDailyCost?: number;
    dailyAccommodationPayment?: number;
}

export function householdDailyCost(first: CareCostTotals | null | undefined, second: CareCostTotals | null | undefined): number {
    return (Number(first?.totalDailyCost) || 0) + (Number(second?.totalDailyCost) || 0);
}

/** The first schedule with the household's daily total and DAP; the first schedule itself without a second. */
export function withHouseholdTotals<T extends CareCostTotals>(first: T, second: CareCostTotals | null | undefined): T {
    if (!second) return first;
    return {
        ...first,
        totalDailyCost: householdDailyCost(first, second),
        dailyAccommodationPayment: (Number(first.dailyAccommodationPayment) || 0) + (Number(second.dailyAccommodationPayment) || 0),
        /** Set so a reader can tell a summed schedule from a single room's. */
        householdOfTwo: true,
    };
}

/**
 * A schedule with the daily payment REPLACED by the one the resident actually pays (fixed when their
 * agreement was struck, not today's MPIR on today's balance), the total restated. For the home
 * someone is in now (8 Oct 2026).
 */
export function withDailyPaymentOverride<T extends CareCostTotals & { basicDailyFee?: number; dailyNCCC?: number; dailyHSC?: number; dailyMTCF?: number; dailyExtraServiceFee?: number }>(costs: T, dailyPayment: number | null | undefined): T {
    if (dailyPayment == null || !Number.isFinite(Number(dailyPayment))) return costs;
    const dap = Math.max(0, Number(dailyPayment) || 0);
    const total = (Number(costs.basicDailyFee) || 0) + dap + (Number(costs.dailyNCCC) || 0) + (Number(costs.dailyHSC) || 0) + (Number(costs.dailyMTCF) || 0) + (Number(costs.dailyExtraServiceFee) || 0);
    return { ...costs, dailyAccommodationPayment: dap, totalDailyCost: total };
}

/**
 * A Suggestion Engine row's daily cost for the household in its FIRST year: its own room, plus the
 * second room where both enter together. A second person who enters later is not in that year — the
 * row's `phaseTwo` carries the household from then (strategyPhaseTwoDailyCost).
 */
export function strategyHouseholdDailyCost(strategy: { strategySpecificAgedCareCosts?: unknown; secondRoom?: { agedCareCosts?: unknown; entersLater?: boolean } | null }, fallback?: CareCostTotals | null): number {
    const first = (strategy.strategySpecificAgedCareCosts as CareCostTotals | undefined) || fallback || null;
    const second = strategy.secondRoom && !strategy.secondRoom.entersLater ? (strategy.secondRoom.agedCareCosts as CareCostTotals | undefined) : null;
    return householdDailyCost(first, second);
}

/** The household's daily cost from the second person's entry, where they enter later; null otherwise. */
export function strategyPhaseTwoDailyCost(strategy: { phaseTwo?: { householdDailyCost?: number } | null }): number | null {
    const v = strategy.phaseTwo?.householdDailyCost;
    return typeof v === 'number' && Number.isFinite(v) ? v : null;
}
